import React from 'react';

const POLLING_INTERVAL_MS = 2500; // 2.5 seconds
const MAX_POLLING_ATTEMPTS = 5;

type UseCompareBatchPollingProps = {
  refetchGetBatchCompareDataQuery: () => Promise<any>;
};

export const useCompareBatchPolling = ({ refetchGetBatchCompareDataQuery }: UseCompareBatchPollingProps) => {
  const [isSynchronizing, setIsSynchronizing] = React.useState(false);
  const pollingCountRef = React.useRef(0);
  const pollingIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const startPolling = React.useCallback(() => {
    pollingCountRef.current = 0;
    setIsSynchronizing(true);

    pollingIntervalRef.current = setInterval(async () => {
      pollingCountRef.current += 1;

      try {
        const result = await refetchGetBatchCompareDataQuery();

        if (result.isSuccess && result.data) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsSynchronizing(false);
          pollingCountRef.current = 0;
        }
      } catch (error) {
        // Continue polling on error
      }

      // Stop after max attempts
      if (pollingCountRef.current >= MAX_POLLING_ATTEMPTS) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setIsSynchronizing(false);
        pollingCountRef.current = 0;
      }
    }, POLLING_INTERVAL_MS);
  }, [refetchGetBatchCompareDataQuery]);

  React.useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return {
    isSynchronizing,
    startPolling,
  };
};
